import { BellIcon } from '@phosphor-icons/react'
import { statusOf, type Notification } from '@robotensor/api'
import { Popover } from '@robotensor/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useState, type ReactElement } from 'react'
import { api } from '../lib/api.ts'
import { queries } from '../lib/queries.ts'
import { Chip, type ChipTone } from './parts.tsx'

/** The chip a notification row starts with: the result's status, Fix, Paid, or Security. A kind or status this build
 * doesn't know still gets a row (the server's title and body) with a plain chip: the status in words, else New. */
export function noteChip(n: Pick<Notification, 'kind' | 'category' | 'data'>): { text: string; tone: ChipTone } {
  const k = n.kind
  const d = (n.data ?? {}) as { status?: string; status_label?: string; points?: number; practice?: boolean; practice_reason?: string }
  const base = k.replace(/^(contribution|rewards|security)\./, '')
  if (base === 'fix_requested' || base === 'needs_correction') return { text: 'Fix', tone: 'fix' }
  if (n.category === 'security' || k.startsWith('security.') || ['device_added', 'wallet_changed', 'call_declined'].includes(base)) return { text: 'Security', tone: 'no' }
  if (['week_paid', 'withdrawal_arrived'].includes(base) || k.startsWith('payout')) return { text: 'Paid', tone: 'chk' }
  const s = statusOf({ status: d.status ?? (k.startsWith('contribution.') ? k.slice('contribution.'.length) : k === 'result' ? 'accepted' : ''), points: d.points, practice: d.practice, practice_reason: d.practice_reason, status_label: d.status_label })
  if (s?.key === 'accepted') return { text: s.points ? `+${s.points}` : 'Accepted', tone: 'ok' }
  if (s?.key === 'practice') return { text: 'Practice', tone: 'prac' }
  if (s?.key === 'rejected') return { text: 'Not accepted', tone: 'no' }
  if (s?.key === 'checking') return { text: 'Checking', tone: 'chk' }
  if (s?.key === 'fix') return { text: 'Fix', tone: 'fix' }
  if (s?.key === 'other' && s.label) return { text: s.label, tone: 'neutral' }
  return { text: 'New', tone: 'new' }
}

/** Where a row goes: only an in-app path this build has a page for (`knows`); anything else is plain text. */
export function noteHref(link: string | null | undefined, knows: (pathname: string) => boolean): string | null {
  if (!link || !link.startsWith('/') || link.startsWith('//')) return null
  const pathname = link.split(/[?#]/)[0] || '/'
  return knows(pathname) ? link : null
}

/** Today / Earlier. Call notifications are push only and never listed. */
export function groupNotes(items: readonly Notification[], now = new Date()): { today: Notification[]; earlier: Notification[] } {
  const start = new Date(now.toDateString()).getTime()
  const shown = items.filter((n) => n.kind !== 'call')
  return { today: shown.filter((n) => new Date(n.created_at).getTime() >= start), earlier: shown.filter((n) => new Date(n.created_at).getTime() < start) }
}

/** "10:36" today, "Sun" before. (Old links, `/activity/<id>`, still work: the router sends them on.) */
function timeOf(iso: string, today: boolean): string {
  const d = new Date(iso)
  return today ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('en-GB', { weekday: 'short' })
}

/** The bell (W10): a drawer grouped Today / Earlier, a status chip per row; a row opens its result page. */
export function Notifications(): ReactElement | null {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const router = useRouter()
  const knows = (pathname: string) => !!router.getMatchedRoutes(pathname)[2]
  const qc = useQueryClient()
  const notes = useQuery({ ...queries.notifications(), retry: false })
  const read = useMutation({
    mutationFn: (ids: string[] | 'all') => api.me.markRead(ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  })
  if (notes.isError) return null // module not deployed: no bell rather than a dead control
  const unread = notes.data?.unread ?? 0
  const { today, earlier } = groupNotes(notes.data?.items ?? [])
  const openNote = (n: Notification, href: string) => {
    if (!n.read_at) read.mutate([n.id])
    setOpen(false)
    void navigate({ href })
  }
  const row = (n: Notification, isToday: boolean) => {
    const chip = noteChip(n)
    const href = noteHref(n.link, knows)
    const inner = (
      <>
        <Chip tone={chip.tone}>{chip.text}</Chip>
        <span className="w-note__text">
          <b>{n.title}</b>
          {n.body ? <span className="w-note__body">{n.body}</span> : null}
          <small>{timeOf(n.created_at, isToday)}</small>
        </span>
      </>
    )
    if (!href)
      return (
        <div key={n.id} className="w-note" data-plain="" data-unread={n.read_at ? undefined : ''}>
          {inner}
        </div>
      )
    return (
      <button key={n.id} type="button" className="w-note" data-unread={n.read_at ? undefined : ''} onClick={() => openNote(n, href)}>
        {inner}
      </button>
    )
  }
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      trigger={
        <button type="button" className="w-iconbtn" data-open={open ? '' : undefined} aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}>
          <BellIcon weight="regular" aria-hidden />
          {unread ? <i className="w-badge" aria-hidden /> : null}
        </button>
      }
    >
      <div className="w-pop w-notes">
        <div className="w-row">
          <b className="w-grow">Notifications</b>
          {unread ? (
            <button type="button" className="w-linkbtn" onClick={() => read.mutate('all')} disabled={read.isPending}>
              Mark all read
            </button>
          ) : null}
        </div>
        {today.length === 0 && earlier.length === 0 ? (
          <p className="w-pop__text">Nothing yet. Results, fixes, payouts and security alerts arrive here and on your phone.</p>
        ) : null}
        {today.length ? <span className="w-eyebrow">Today</span> : null}
        {today.map((n) => row(n, true))}
        {earlier.length ? <span className="w-eyebrow">Earlier</span> : null}
        {earlier.map((n) => row(n, false))}
      </div>
    </Popover>
  )
}
